import styled, {css} from 'styled-components';

import {IconSide} from 'components/design-guide';
import ActionLoadingSpinner from 'components/design-guide/Action/Button/LoadingSpinner';
import {
  ActionStyleIcon,
  ActionStyleImage,
  ActionStyleStyledIcon,
  ActionStyleText
} from 'components/design-guide/Action/Hyperlink/types';
import {FontSize, Spacing} from 'styles/Base';
import {Colors, Timing} from 'styles/Colors';
import {generateMargins, MarginProps} from 'utils/components';

const disabledTextStyle = css`
  color: ${Colors.DISABLED_GREY};
  cursor: not-allowed;
  text-decoration: none;
`;

export const ActionStyleTextComponent = styled.span<ActionStyleText>`
  ${({styleProps}) => {
    const {fontSize, lineHeight} = styleProps ?? {};
    const defaultFontStyle = css`
      font-size: inherit;
      line-height: ${lineHeight ?? 'inherit'};
    `;

    switch (fontSize) {
      case FontSize.ONE:
        return css`
          font-size: ${FontSize.ONE};
          line-height: ${lineHeight ?? Spacing.ONE_5};
        `;
      case FontSize.ZERO_875:
        return css`
          font-size: ${FontSize.ZERO_875};
          line-height: ${lineHeight ?? Spacing.ONE_3125};
        `;
      case FontSize.ZERO_8125:
        return css`
          font-size: ${FontSize.ZERO_8125};
          line-height: ${lineHeight ?? Spacing.ONE_25};
        `;
      case 'inherit':
        return defaultFontStyle;
      default:
        return defaultFontStyle;
    }
  }}
  transition: color 0.2s ${Timing.COLOR_TRANSITION};
  cursor: pointer;
`;

const IconLinkTextSpan = styled.span<{disabled?: boolean; fontSize?: FontSize; isStyledIcon?: boolean}>`
  color: ${Colors.PINE_GREEN};
  transition: color 0.2s ${Timing.COLOR_TRANSITION};
  cursor: pointer;
  text-decoration: none;
  font-size: ${({fontSize}) => fontSize ?? 'inherit'};
  text-align: left;
  ${({isStyledIcon}) => isStyledIcon && 'display: flex; align-items: center;'}
  ${({disabled}) => (disabled ? disabledTextStyle : '')}
`;

const Icon = styled.img<{
  iconSide: IconSide;
  iconMargin?: MarginProps;
  verticalAlign?: string;
  iconHeight?: string;
  iconWidth?: string;
  padding?: string;
}>`
  ${({iconMargin, iconSide}) => css`
    ${iconMargin
      ? generateMargins(iconMargin)
      : `margin: ${Spacing.ZERO} ${iconSide === IconSide.LEFT ? Spacing.ZERO_5 : Spacing.ZERO} ${Spacing.ZERO} ${
          iconSide === IconSide.RIGHT ? Spacing.ZERO_5 : Spacing.ZERO
        } `};
  `}
  ${({verticalAlign}) => css`
    vertical-align: ${verticalAlign ? verticalAlign : 'middle'};
  `}
  
  ${({iconHeight}) => css`
    ${iconHeight && `height: ${iconHeight} `};
  `}
  ${({iconWidth}) => css`
    ${iconWidth && `width: ${iconWidth} `};
  `}
    ${({padding}) => css`
    ${padding && `padding: ${padding} `};
  `}
`;

export const ActionStyleIconComponent = ({
  styleProps: {icon, alt = '', iconMargin, verticalAlign, iconHeight, iconWidth, fontSize, iconSide = IconSide.LEFT},
  disabled,
  children
}: ActionStyleIcon) => (
  <IconLinkTextSpan {...{fontSize, disabled}}>
    {iconSide === IconSide.LEFT && (
      <Icon src={icon} {...{alt, iconMargin, verticalAlign, iconHeight, iconWidth, iconSide}} aria-hidden={!alt} />
    )}
    {children}
    {iconSide === IconSide.RIGHT && (
      <Icon src={icon} {...{alt, iconMargin, verticalAlign, iconHeight, iconWidth, iconSide}} aria-hidden={!alt} />
    )}
  </IconLinkTextSpan>
);

export const ActionStyleStyledIconComponent = ({
  styleProps: {icon, fontSize, loadingSpinner, iconSide = IconSide.LEFT},
  disabled,
  children
}: ActionStyleStyledIcon) => {
  if (loadingSpinner?.active) {
    return (
      <IconLinkTextSpan>
        <ActionLoadingSpinner active buttonStyle={loadingSpinner.style} includeButtonPadding={false} />
      </IconLinkTextSpan>
    );
  }
  return (
    <IconLinkTextSpan {...{fontSize, disabled, isStyledIcon: true}}>
      {iconSide === IconSide.LEFT && icon}
      {children}
      {iconSide === IconSide.RIGHT && icon}
    </IconLinkTextSpan>
  );
};

export const ActionStyleImageComponent = ({
  styleProps: {src, alt, height, width, loadingSpinner, padding},
  disabled
}: ActionStyleImage) => {
  if (loadingSpinner?.active) {
    return (
      <IconLinkTextSpan>
        <ActionLoadingSpinner active buttonStyle={loadingSpinner.style} includeButtonPadding={false} />
      </IconLinkTextSpan>
    );
  }
  return (
    <IconLinkTextSpan>
      <Icon
        src={src}
        iconHeight={height}
        iconWidth={width}
        iconMargin={{margin: Spacing.ZERO}}
        iconSide={IconSide.LEFT}
        {...{alt}}
        aria-hidden={!alt}
        padding={padding}
      />
    </IconLinkTextSpan>
  );
};
